import { useEffect, useState } from 'react'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { extractTextFromPDF, fileToBase64, type ParserProvider, type PdfExtractResult } from '../../api/commercialInvoicePdf'

const PROVIDERS: { key: ParserProvider; label: string }[] = [
  { key: 'gemini', label: 'Gemini' },
  { key: 'anthropic', label: 'Anthropic (native PDF)' },
]

// Approximate list prices, USD per 1M tokens (input / output). Estimates only -- check the
// vendors' pricing pages before relying on them for budgeting. Unknown model -> no estimate.
const PRICE_PER_MTOK: Record<string, [number, number]> = {
  'gemini-2.0-flash': [0.10, 0.40],
  'claude-sonnet-4-5': [3, 15],
}

function estimateCost(r: PdfExtractResult): string {
  const price = r.model ? PRICE_PER_MTOK[r.model] : undefined
  const { inputTokens, outputTokens } = r.usage ?? { inputTokens: null, outputTokens: null }
  if (!price || inputTokens == null || outputTokens == null) return 'n/a'
  const usd = (inputTokens * price[0] + outputTokens * price[1]) / 1_000_000
  return `$${usd.toFixed(4)}`
}

type Outcome = { result: PdfExtractResult } | { error: string }

export default function CiParserComparePage() {
  const { userEmail } = useAuth()
  const [defaultProvider, setDefaultProvider] = useState<ParserProvider>('gemini')
  const [savedMsg, setSavedMsg] = useState<string | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [running, setRunning] = useState(false)
  const [outcomes, setOutcomes] = useState<Partial<Record<ParserProvider, Outcome>>>({})

  useEffect(() => {
    supabase.from('company_settings').select('ci_parser_provider').eq('id', 'default').maybeSingle()
      .then(({ data }) => { if (data?.ci_parser_provider) setDefaultProvider(data.ci_parser_provider as ParserProvider) })
  }, [])

  async function saveDefault(p: ParserProvider) {
    setDefaultProvider(p)
    const { error } = await supabase.from('company_settings')
      .update({ ci_parser_provider: p, updated_by: userEmail }).eq('id', 'default')
    setSavedMsg(error ? `Error: ${error.message}` : `Default set to ${p} at ${new Date().toLocaleTimeString('en-GB')}`)
  }

  async function compare(file: File) {
    setFileName(file.name)
    setRunning(true)
    setOutcomes({})
    const base64 = await fileToBase64(file)
    // Both providers run at the same time; each reports its own server-side duration.
    await Promise.all(PROVIDERS.map(async ({ key }) => {
      let outcome: Outcome
      try { outcome = { result: await extractTextFromPDF(base64, key) } }
      catch (e) { outcome = { error: e instanceof Error ? e.message : String(e) } }
      setOutcomes(prev => ({ ...prev, [key]: outcome }))
    }))
    setRunning(false)
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white">CI PDF Parser</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        Which AI reads Commercial Invoice PDFs. Switch the default instantly, or run one PDF
        through both and compare. Nothing is saved from a comparison run.
      </p>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Default provider (used by all staff uploads)</label>
        <select value={defaultProvider} onChange={e => saveDefault(e.target.value as ParserProvider)}
          className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white">
          {PROVIDERS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        {savedMsg && <span className="ml-3 text-xs text-gray-500 dark:text-gray-400">{savedMsg}</span>}
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Compare: upload a PDF</label>
        <input type="file" accept="application/pdf" disabled={running}
          onChange={e => { const f = e.target.files?.[0]; if (f) void compare(f); e.target.value = '' }}
          className="text-sm text-gray-700 dark:text-gray-300" />
        {running && <span className="ml-3 text-sm text-gray-500">Running both providers on {fileName}…</span>}
      </div>

      {fileName && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {PROVIDERS.map(({ key, label }) => {
            const o = outcomes[key]
            return (
              <div key={key} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h2 className="font-semibold text-gray-800 dark:text-white mb-2">{label}</h2>
                {!o && <p className="text-sm text-gray-500">Running…</p>}
                {o && 'error' in o && <p className="text-sm text-red-600 dark:text-red-400 break-words">{o.error}</p>}
                {o && 'result' in o && (
                  <>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300 mb-3">
                      <dt>Model</dt><dd>{o.result.model}</dd>
                      <dt>Time</dt><dd>{o.result.durationMs != null ? `${(o.result.durationMs / 1000).toFixed(1)} s` : 'n/a'}</dd>
                      <dt>Tokens in / out</dt><dd>{o.result.usage?.inputTokens ?? '?'} / {o.result.usage?.outputTokens ?? '?'}</dd>
                      <dt>Est. cost</dt><dd>{estimateCost(o.result)}</dd>
                      <dt>Confidence</dt><dd>{o.result.confidence}</dd>
                      <dt>Characters</dt><dd>{o.result.rawText.length}</dd>
                    </dl>
                    <textarea readOnly value={o.result.rawText} rows={24}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 text-xs font-mono dark:bg-gray-900 dark:text-gray-100" />
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
